import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { captureAttribution } from "@/lib/attribution";

const SiteContext = createContext(null);

export const DEFAULT_ENABLED = ["commercial", "home", "powerwall", "roofing", "solar-roof", "construction", "hvac", "insulation", "ev", "smart-panel", "supercharger", "care"];

const DEFAULT_CONTENT = {
  hero: {
    headline: "Turn your commercial electric bill into a smarter investment.",
    sub: "Explore potential energy savings, available tax benefits, battery demand-charge reduction, and ownership or financing options—with an assessment built around your property and utility bill.",
    ctaPrimary: "Upload Your Commercial Electric Bill",
    ctaSecondary: "Explore Your Solar ROI",
  },
  about: {
    intro: "Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform, providing project assessments, financial calculators, utility-bill reviews, and solar-plus-storage planning. Lawrence Williams leads commercial sales.",
    role: "Lawrence Williams leads commercial sales for Commercial Solar ROI. Commercial inquiries submitted through this platform reach him and the commercial sales team.",
    relationship: "Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform. It houses the commercial education, utility-bill reviews, financial calculators, project assessments and sales journey — all leads stay on commercialsolarroi.com.",
    responsibilities: "Coastal Energy is the company behind this platform and is responsible for the services delivered through it. Who designs, contracts, installs and services each project is performed through Coastal Energy's own service organization.",
    territories: "Commercial and residential solar and storage availability is confirmed per property during the assessment. Commercial and residential solar work is handled on this site — call or text 310-881-5155.",
    credentials: "Licenses and certifications applicable to your project belong to Coastal Energy and are stated with the services they apply to. Only credentials that can be verified are listed — nothing is invented.",
    warranties: "Workmanship, equipment and service warranty terms applicable to your project are documented in your Coastal Energy proposal.",
  },
  commercialFaqs: [],
  followUpNote: "We'll confirm the next steps and review timeline after receiving your bill.",
  responseTimeNote: "",
  assumptionSources: "",
};

function mergeContent(loaded) {
  const clean = loaded || {};
  return {
    ...DEFAULT_CONTENT,
    ...clean,
    hero: { ...DEFAULT_CONTENT.hero, ...(clean.hero || {}) },
    about: { ...DEFAULT_CONTENT.about, ...(clean.about || {}) },
  };
}

export function SiteProvider({ children }) {
  const [config, setConfig] = useState({
    brand: "Solar Black Belt",
    brandLine: "A COASTAL ENERGY PROJECT",
    phone: "310-881-5155",
    phoneHref: "tel:+13108815155",
    tagline: "Smarter energy starts with a clearer picture.",
    enabledServices: DEFAULT_ENABLED,
    coverageNote: "",
    policyVersion: "2026-10",
  });
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [lead, setLead] = useState({ open: false, serviceId: "commercial", contextService: "", extra: {} });
  const [info, setInfo] = useState({ open: false, title: "", kicker: "SOLAR BLACK BELT", body: "" });
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditSnapshot, setAuditSnapshot] = useState(null);
  const [auditFiles, setAuditFiles] = useState([]);
  const [funnel, setFunnel] = useState({});

  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    let alive = true;
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d && d.brand) setConfig(d);
      })
      .catch(() => {});
    fetch(`${base}/api/content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setContent(mergeContent(d));
      })
      .catch(() => {});
    fetch(`${base}/api/settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d) setContent((c) => ({ ...c, followUpNote: d.followUpNote || c.followUpNote, responseTimeNote: d.responseTimeNote || "", assumptionSources: d.assumptionSources || c.assumptionSources }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const openLead = useCallback((serviceId, contextService, extra = {}) => {
    setLead({ open: true, serviceId, contextService, extra });
  }, []);
  const closeLead = useCallback(() => setLead((l) => ({ ...l, open: false })), []);
  const openInfo = useCallback((title, body, kicker = "SOLAR BLACK BELT") => {
    setInfo({ open: true, title, body, kicker });
  }, []);
  const closeInfo = useCallback(() => setInfo((i) => ({ ...i, open: false })), []);
  const openAudit = useCallback((snapshot = null, files = []) => {
    setAuditSnapshot(snapshot || null);
    setAuditFiles(files || []);
    setAuditOpen(true);
  }, []);
  const closeAudit = useCallback(() => setAuditOpen(false), []);
  const setFunnelExtra = useCallback((partial) => setFunnel((f) => ({ ...f, ...partial })), []);

  const value = useMemo(
    () => ({ config, content, lead, openLead, closeLead, info, openInfo, closeInfo, auditOpen, auditSnapshot, openAudit, closeAudit, funnel, setFunnelExtra }),
    [config, content, lead, openLead, closeLead, info, openInfo, closeInfo, auditOpen, auditSnapshot, openAudit, closeAudit, funnel, setFunnelExtra],
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}
